// AutoTaggingRulesTab.js
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, InputAdornment, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography,
  Tooltip, Switch, FormControl, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery
} from '@mui/material';
import { Add, Save, Close, BorderColorOutlined as Edit, Search } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { DataAPIContext } from '../api';
import { CREATE, DELETE, UPDATE } from '../api/dataProvider';
import { ReloadIcon } from '../components/actionIcons';
import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { ContextStore } from '../store/contextStore';
import { isOk, serverErrorMessage } from '../utils';
import { alpha, useTheme } from '@mui/material/styles';

/* ========= constants ========= */
const TAGS_RESOURCE = 'tour_tag';
const INTERNAL_TAG_TYPE = 'chat_internal'; // strict internal tag type

/* ========= tag helpers (normalize to {id, name}) ========= */
const normalizeTag = (t) => {
  if (!t) return { id: null, name: '' };
  if (typeof t === 'string') return { id: null, name: t };
  if (typeof t === 'object') {
    const id = t.id ?? t._id ?? t.value ?? t?.data?.id ?? t?.pk ?? null;
    const name = t.name ?? t.label ?? t?.data?.name ?? t?.text ?? '';
    return { id, name };
  }
  return { id: null, name: String(t) };
};
const tagToName = (t) => normalizeTag(t).name;
const tagToId = (t) => normalizeTag(t).id;
const tagsEqual = (a, b) => {
  const aid = tagToId(a); const bid = tagToId(b);
  if (aid && bid) return String(aid) === String(bid);
  return tagToName(a).toLowerCase() === tagToName(b).toLowerCase();
};

/* ========= mapping helpers ========= */
const uiToServerTrigger = (s) => {
  const v = (s || '').toLowerCase();
  if (v.includes('intent')) return 'intent';
  if (v.includes('sentiment')) return 'sentiment';
  return 'keyword';
};
const uiToServerTagType = (s) => {
  const v = (s || '').toLowerCase();
  return v.includes('participant') ? 'participant' : 'internal';
};

const isSentimentType = (s) => (s || '').toLowerCase().includes('sentiment');

const isValidSentimentValue = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 10;
};

/* ========= validation ========= */
const isRuleValid = (r) => {
  const val = (r?.trigger_value || '').trim();
  const tag = normalizeTag(r?.tag_to_apply);
  if (isSentimentType(r?.trigger_type) && !isValidSentimentValue(val)) return false;
  return Boolean(val) && Boolean(tag.id);
};

function AutoTaggingRulesTab({ licenseeId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const dataAPI = useContext(DataAPIContext);
  const resource = 'tour_operator/auto_tagging_rules';
  const { hasPerm } = useContext(ContextStore);
  const hasAIlistPerm = hasPerm('ai', 'list');
  const hasAIwritePerm = hasPerm('ai', 'write');
  const hasAIdeletePerm = hasPerm('ai', 'delete');

  const [enabled, setEnabled] = useState(JSON.parse(window.localStorage.getItem('bootstrap')).tour_operator['config']['auto_tagging_enabled']);
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('trigger_type');

  const [editingId, setEditingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [selection, setSelection] = useState({});

  // show errors after save attempt (per-row)
  const [attemptedSaveById, setAttemptedSaveById] = useState({});
  const markAttemptedSave = (id) => setAttemptedSaveById((m) => ({ ...m, [id]: true }));
  const clearAttemptedSave = (id) => setAttemptedSaveById((m) => { const n = { ...m }; delete n[id]; return n; });
  const shouldShowErrors = (id) => !!attemptedSaveById[id];

  // per-row RemoteAutocomplete input state (not the selected tag)
  const [tagQueryByRule, setTagQueryByRule] = useState({});
  const [tagOptionsByRule, setTagOptionsByRule] = useState({});

  const [acKeyByRule, setAcKeyByRule] = useState({});

  // Confirm delete state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [pendingRule, setPendingRule] = useState(null);

  const openConfirm = (rule) => { setPendingRule(rule); setConfirmOpen(true); };
  const closeConfirm = () => { setConfirmOpen(false); setPendingRule(null); };

  const openBulkConfirm = () => setConfirmBulkOpen(true);
  const closeBulkConfirm = () => setConfirmBulkOpen(false);
  
  const updateDraft = useCallback((id, patchOrFn) => {
    setDrafts(prev => {
      const base = prev[id] ?? {};
      const patch = typeof patchOrFn === 'function' ? patchOrFn(base) : patchOrFn;
      return { ...prev, [id]: { ...base, ...patch } };
    });
  }, []);

  const ensureOptionPresent = useCallback( // ADD: ensure selected tag appears in options
    (ruleId, tag) => {
      const obj = normalizeTag(tag);
      if (!obj.id && !obj.name) return;
      setTagOptionsByRule(prev => {
        const list = prev[ruleId] || [];
        const exists = list.some(opt => tagsEqual(opt, obj));
        return exists ? prev : { ...prev, [ruleId]: [obj, ...list] };
      });
    },
    []
  );

  // Filter rules based on search query
  const filteredRules = React.useMemo(() => {
    if (!searchQuery.trim()) return rules;
    
    const query = searchQuery.toLowerCase();
    return rules.filter(rule => {
      // Always show the row being edited, even if it doesn't match the search
      if (editingId && rule.id === editingId) return true;
      
      // Search in trigger type
      if (rule.trigger_type?.toLowerCase().includes(query)) return true;
      // Search in trigger value
      if (rule.trigger_value?.toLowerCase().includes(query)) return true;
      // Search in tag type
      if (rule.tag_type?.toLowerCase().includes(query)) return true;
      // Search in tag name
      if (tagToName(rule.tag_to_apply)?.toLowerCase().includes(query)) return true;
      return false;
    });
  }, [rules, searchQuery, editingId]);

  const allSelected = useMemo(
    () => filteredRules.length && Object.values(selection).filter(Boolean).length === filteredRules.length,
    [filteredRules, selection]
  );
  const toggleAll = (checked) =>
    setSelection(checked ? Object.fromEntries(filteredRules.map((r) => [r.id, true])) : {});
  const toggleOne = (id) => setSelection((s) => ({ ...s, [id]: !s[id] }));

  const toggleRuleEnabled = async (rule, checked) => {
    // optimistic
    setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_enabled: checked } : r));
    if (drafts[rule.id]) updateDraft(rule.id, { is_enabled: checked });

    // don't hit API for unsaved local rows
    if (String(rule.id).startsWith('local-')) return;

    try {
      await dataAPI?.(UPDATE, resource, { id: rule.id, data: { is_enabled: checked } });
      enqueueSnackbar(checked ? 'Rule enabled' : 'Rule disabled', { variant: 'success' });
    } catch (e) {
      // revert on failure
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_enabled: !checked } : r));
      if (drafts[rule.id]) updateDraft(rule.id, { is_enabled: !checked });
      enqueueSnackbar('Failed to update rule status', { variant: 'error' });
    }
  };

  const fetchRules = async () => {
    if (!hasAIlistPerm) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await dataAPI?.('GET_LIST', resource, licenseeId ? { franchise_to: licenseeId } : {});
      if (Array.isArray(res)) {
        const mapped = res.map((r) => ({
          id: r.id ?? r._id,
          trigger_type: r.trigger_type ? String(r.trigger_type).charAt(0).toUpperCase() + String(r.trigger_type).slice(1) : 'Keyword',
          trigger_value: r.trigger_value ?? '',
          tag_type: r.tag_type === 'participant' ? 'Participant tag' : 'Internal tag',
          // backend might send id or object; normalize
          tag_to_apply: normalizeTag(r.tag_to_apply ?? r.tag ?? r.tag_to_apply_id),
          createdAt: r.created_ts ? new Date(r.created_ts).getTime() : Date.now(),
          is_enabled: r.is_enabled !== false, // default true
        }));
        setRules(mapped);
      }
    } catch {
      // keep demo data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); /* eslint-disable-line */ }, [licenseeId]);

  const startEdit = (rule) => {
    setEditingId(rule.id);
    updateDraft(rule.id, { ...rule, tag_to_apply: normalizeTag(rule.tag_to_apply) });
    clearAttemptedSave(rule.id);
    setTagQueryByRule((q) => ({ ...q, [rule.id]: '' }));
    setTagOptionsByRule((o) => ({ ...o, [rule.id]: [] }));
  };

  const cancelEdit = (id) => {
    if (!id) return;
    const isLocal = String(id).startsWith('local-');

    if (isLocal) {
      // drop the temp row entirely
      setRules(prev => prev.filter(r => r.id !== id));
    }
    // discard any draft/UI state
    clearRowUiState(id);
    setEditingId(null);
  };

  // create an internal tag, return {id, name}
    const createInternalTag = async (name) => {
      const payload = { name, category: INTERNAL_TAG_TYPE };
      const res = await dataAPI?.(CREATE, TAGS_RESOURCE, { data: payload });
      const raw = res?.data ?? res;
      const t = normalizeTag(raw?.data ?? raw);

      if (!t || !t.id) { throw new Error('Tag creation returned no id'); }

      return { id: t.id, name: t.name || name };
    };

  const saveRule = async (id) => {
    let draft = drafts[id];
    const isInternal = (draft.tag_type || '').toLowerCase().includes('internal');

      if (isSentimentType(draft.trigger_type) && !isValidSentimentValue(draft.trigger_value)) {
        markAttemptedSave(id);
        enqueueSnackbar('Sentiment must be a whole number from 1 to 10', { variant: 'warning' });
        return;
      }

    // if internal and user typed a new name, create it first to ensure we send an id
    let tagObj = normalizeTag(draft.tag_to_apply);
    if (isInternal && !tagObj.id && tagObj.name) {
      try {
        const created = await createInternalTag(tagObj.name);
        tagObj = created;
        updateDraft(id, { tag_to_apply: created });
      } catch {
        markAttemptedSave(id);
        enqueueSnackbar('Could not create internal tag', { variant: 'error' });
        return;
      }
    }

    // validate after potential creation
    draft = { ...draft, tag_to_apply: tagObj };
    if (!isRuleValid(draft)) {
      markAttemptedSave(id);
      enqueueSnackbar('Please fill Trigger Value and Tag to Apply', { variant: 'warning' });
      return;
    }

    // optimistic UI
    setRules((prev) => prev.map((r) => (r.id === id ? { ...draft } : r)));
    setEditingId(null);
    clearAttemptedSave(id);

    try {
      const payload = {
        trigger_type: uiToServerTrigger(draft.trigger_type),
        trigger_value: isSentimentType(draft.trigger_type) ? String(Number(draft.trigger_value)) : draft.trigger_value,
        tag_type: uiToServerTagType(draft.tag_type),
        tag_to_apply: tagObj.id,
        is_enabled: !!draft.is_enabled
      };

      if (String(id).startsWith('local-')) {
        const createData = licenseeId ? { ...payload, tour_operator: licenseeId } : payload;
        const res = await dataAPI?.(CREATE, resource, { data: createData });
        if (res?.id) {
          setRules((prev) => prev.map((r) => (r.id === id ? { ...draft, id: res.id } : r)));
        }
      } else {
        await dataAPI?.(UPDATE, resource, { id, data: payload });
      }
      enqueueSnackbar('Rule saved', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to save rule', { variant: 'error' });
      fetchRules(); // rollback
    }
  };

  const deleteRule = async (rule) => {
    const prev = rules;
    setRules((r) => r.filter((x) => x.id !== rule.id));
    try {
      if (!String(rule.id).startsWith('demo-') && !String(rule.id).startsWith('local-')) {
        await dataAPI?.(DELETE, resource, { id: rule.id });
      }
      enqueueSnackbar('Rule deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to delete', { variant: 'error' });
      setRules(prev);
    }
  };

  const bulkDelete = async () => {
    const ids = Object.entries(selection).filter(([, v]) => v).map(([id]) => id);
    if (!ids.length) return;
    const prev = rules;
    setRules((r) => r.filter((x) => !ids.includes(x.id)));
    setSelection({});
    try {
      await Promise.all(ids.map((id) =>
        (String(id).startsWith('demo-') || String(id).startsWith('local-'))
          ? Promise.resolve()
          : dataAPI?.(DELETE, resource, { id }).catch(() => null)
      ));
      enqueueSnackbar('Selected rules deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Some deletions may have failed', { variant: 'warning' });
      setRules(prev);
    }
  };

  const addRule = () => {
    const now = Date.now();
    const id = `local-${now}`;
    const newRule = {
      id,
      trigger_type: 'Keyword',
      trigger_value: '',
      tag_type: 'Internal tag',
      tag_to_apply: { id: null, name: '' },
      createdAt: now,
      is_enabled: true
    };
    setRules((r) => [newRule, ...r]);
    startEdit(newRule);
  };

  const saveEnabled = async (checked) => {
    setEnabled(checked);
    try {
      const res = await dataAPI?.(UPDATE, `tour_operator`, { id: licenseeId || JSON.parse(window.localStorage.getItem('bootstrap')).tour_operator['id'], data: { config: { auto_tagging_enabled: checked } } });
      // A refused save resolves with the error body: nothing changed, so neither does the cache
      if (!isOk(res)) {
        setEnabled(!checked);
        enqueueSnackbar(serverErrorMessage(res, 'Failed to update auto-tagging status'), { variant: 'error' });
        return;
      }
      localStorage.setItem('bootstrap', JSON.stringify({
        ...JSON.parse(localStorage.getItem('bootstrap')),
        tour_operator: {
          ...JSON.parse(localStorage.getItem('bootstrap')).tour_operator,
          config: {
            ...JSON.parse(localStorage.getItem('bootstrap')).tour_operator.config,
            auto_tagging_enabled: checked
          }
        }
      }));
      enqueueSnackbar(checked ? 'Auto-tagging enabled' : 'Auto-tagging disabled', { variant: 'success' });
    } catch {
      setEnabled(!checked);
      enqueueSnackbar('Failed to update auto-tagging status', { variant: 'error' });
    }
  };

  const columns = [
    { id: 'trigger_type', label: 'Trigger Type' },
    { id: 'trigger_value', label: 'Trigger Value' },
    { id: 'tag_type', label: 'Tag type' },
    { id: 'tag_to_apply', label: 'Tag to Apply' },
    { id: 'is_enabled', label: 'Enabled' },
  ];

  const getComparator = (ord, key) => (a, b) => {
    const ax = key === 'tag_to_apply' ? tagToName(a[key]) : (a[key] ?? '');
    const bx = key === 'tag_to_apply' ? tagToName(b[key]) : (b[key] ?? '');
    if (ax < bx) return ord === 'asc' ? -1 : 1;
    if (ax > bx) return ord === 'asc' ? 1 : -1;
    return 0;
  };

  const stableSort = (array, comparator) =>
    array
      .map((el, index) => [el, index])
      .sort((a, b) => {
        const o = comparator(a[0], b[0]);
        return o !== 0 ? o : a[1] - b[1];
      })
      .map((el) => el[0]);

  const sortedRules = React.useMemo(() => {
    const arr = stableSort(filteredRules, getComparator(order, orderBy));

    // Only bubble to top if it's a brand-new (local) row being edited
    if (editingId && String(editingId).startsWith('local-')) {
      const idx = arr.findIndex(r => r.id === editingId);
      if (idx > 0) {
        const [editingRow] = arr.splice(idx, 1);
        arr.unshift(editingRow);
      }
    }
    return arr;
  }, [filteredRules, order, orderBy, editingId]);


  const clearRowUiState = (id) => {
    setDrafts(prev => { const { [id]: _, ...rest } = prev; return rest; });
    setTagQueryByRule(prev => { const { [id]: _, ...rest } = prev; return rest; });
    setTagOptionsByRule(prev => { const { [id]: _, ...rest } = prev; return rest; });
    setAcKeyByRule(prev => { const { [id]: _, ...rest } = prev; return rest; });
    setSelection(prev => { const { [id]: _, ...rest } = prev; return rest; });
    setAttemptedSaveById(prev => { const { [id]: _, ...rest } = prev; return rest; });
  };

  if (!hasAIlistPerm) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">Access Denied</Typography>
        <Typography>You do not have permission to view auto-tagging rules.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
        <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
          <Typography variant="h6">Auto-Tagging Rules</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Configure how incoming messages can trigger internal or participant tags based on keywords, sentiment, or intent.
          </Typography>
        </Box>

        {/* Right-side Enable/Disable control (styled like a pill) */}
        <Box
          sx={(t) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.5,
            borderRadius: '9px',
            border: '1px solid',
            borderColor: enabled ? 'primary.main' : 'divider',
            bgcolor: 'background.paper',
            transition: 'background-color .15s ease',
            '&:hover': {
              bgcolor: alpha(t.palette.primary.main, 0.03),
            },
          })}
        >
          <Switch
            size="small"
            color="primary"
            checked={enabled}
            onChange={(e) => saveEnabled(e.target.checked)}
            disabled={!hasAIwritePerm}
          />
          <Typography variant="button" sx={{ fontWeight: 500, color: enabled ? 'primary.main' : 'text.secondary' }}>
            ENABLE AUTO-TAGGING
          </Typography>
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box sx={{ maxWidth: 600, flex: '1 1 auto', minWidth: 0 }}>
          <TextField
            variant="filled"
            size="small"
            placeholder="Search rules by trigger type, trigger value, or tag..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: '100%' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              sx: {
                '& input': {
                  py: '10px',
                },
              },
            }}
          />
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            flexWrap: 'wrap',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
          }}
        >
          <Tooltip title="Refresh">
            <IconButton onClick={fetchRules} sx={{ mr: isMobile ? 0 : -1.5, mt: -0.2, order: isMobile ? 2 : 0 }}><ReloadIcon /></IconButton>
          </Tooltip>
          {!isMobile && (
            <DeleteButton sx={{mr: 0.5}} onClick={openBulkConfirm} allowed={hasAIdeletePerm} disabled={!Object.values(selection).some(Boolean)}>Delete Selected</DeleteButton>
          )}
          <Button variant="contained" startIcon={<Add />} onClick={addRule} disabled={!hasAIwritePerm}>New Tagging Rule</Button>
        </Box>
      </Box>

      {hasAIlistPerm && !hasAIwritePerm && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          You have view-only access. Editing and deletes are disabled.
        </Typography>
      )}

      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : isMobile ? (
        // Mobile card view
        <Box sx={{ mb: 2 }}>
          {sortedRules.length === 0 && searchQuery.trim() ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={4}>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                No rules match your search
              </Typography>
            </Box>
          ) : (
            sortedRules.map((rule) => {
              const isEditing = editingId === rule.id;
              const draft = drafts[rule.id] ?? rule;
              const isInternal = (draft.tag_type || '').toLowerCase().includes('internal');

              return (
                <Card key={rule.id} sx={{ mb: 1.5 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Header: trigger summary + actions */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {isEditing ? (
                          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                            <Select
                              value={draft.trigger_type}
                              onChange={(e) => updateDraft(rule.id, { trigger_type: e.target.value })}
                              disabled={!hasAIwritePerm}
                            >
                              <MenuItem value="Keyword">Keyword</MenuItem>
                              <MenuItem value="Sentiment">Sentiment</MenuItem>
                              <MenuItem value="Intent">Intent</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {rule.trigger_type}
                          </Typography>
                        )}
                        {isEditing ? (
                          <TextField
                            size="small"
                            fullWidth
                            required
                            type={isSentimentType(draft.trigger_type) ? 'number' : 'text'}
                            multiline={!isSentimentType(draft.trigger_type)}
                            minRows={2}
                            maxRows={4}
                            inputProps={isSentimentType(draft.trigger_type) ? { min: 1, max: 10, step: 1 } : {}}
                            placeholder={
                              isSentimentType(draft.trigger_type) ? '1 – 10' :
                              (draft.trigger_type === 'Keyword' ? 'e.g., Cancel reservation' : 'Value')
                            }
                            value={draft.trigger_value}
                            onChange={(e) => updateDraft(rule.id, { trigger_value: e.target.value })}
                            disabled={!hasAIwritePerm}
                            error={shouldShowErrors(rule.id) && (
                              !draft.trigger_value?.toString().trim() ||
                              (isSentimentType(draft.trigger_type) && !isValidSentimentValue(draft.trigger_value))
                            )}
                            helperText={shouldShowErrors(rule.id) && (
                              !draft.trigger_value?.toString().trim()
                                ? 'Required'
                                : (isSentimentType(draft.trigger_type) && !isValidSentimentValue(draft.trigger_value))
                                  ? 'Enter a whole number from 1 to 10'
                                  : ''
                            )}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                          >
                            {rule.trigger_value || '—'}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexShrink: 0, gap: 0.5 }}>
                        {isEditing ? (
                          <>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={() => cancelEdit(rule.id)}>
                                <Close fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => saveRule(rule.id)} disabled={!hasAIwritePerm}>
                                <Save fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => startEdit(rule)} disabled={!hasAIwritePerm}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <DeleteIconButton onClick={() => openConfirm(rule)} allowed={hasAIdeletePerm} />
                          </>
                        )}
                      </Box>
                    </Box>

                    {/* Tag type */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0 }}>
                        Tag type
                      </Typography>
                      {isEditing ? (
                        <FormControl size="small" sx={{ flex: 1 }}>
                          <Select
                            value={draft.tag_type}
                            onChange={(e) => {
                              updateDraft(rule.id, {
                                tag_to_apply: null,
                                tag_type: e.target.value,
                                participant_tag_to_apply: null
                              });
                              setTagQueryByRule((q) => ({ ...q, [rule.id]: '' }));
                              setAcKeyByRule((k) => ({ ...k, [rule.id]: (k[rule.id] || 0) + 1 }));
                            }}
                          >
                            <MenuItem value="Internal tag">Internal tag</MenuItem>
                            <MenuItem value="Participant tag">Participant tag</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body2">{rule.tag_type}</Typography>
                      )}
                    </Box>

                    {/* Tag to apply */}
                    <Box sx={{ display: 'flex', alignItems: isEditing ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0, mt: isEditing ? 1 : 0 }}>
                        Tag to apply
                      </Typography>
                      {isEditing ? (
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                        <RemoteAutocomplete
                          disabled={!hasAIwritePerm}
                          key={`${rule.id}-${(draft.tag_type || '').toLowerCase()}-${acKeyByRule[rule.id] || 0}`}
                          resource={TAGS_RESOURCE}
                          apiFilters={[{ field: 'category_code', op: isInternal ? 'equals' : 'not', value: INTERNAL_TAG_TYPE }]}
                          defaultValue={normalizeTag(draft.tag_to_apply)}
                          onChange={(e, v) => {
                            const sel = v ? normalizeTag(v) : null;
                            updateDraft(rule.id, { tag_to_apply: sel });
                            ensureOptionPresent(rule.id, sel);
                          }}
                          setOptionsCount={() => {}}
                          setOptions={(opts) => {
                            const norm = (opts || []).map(normalizeTag);
                            setTagOptionsByRule((o) => ({ ...o, [rule.id]: norm }));
                            ensureOptionPresent(
                              rule.id,
                              drafts[rule.id]?.tag_to_apply || rule.tag_to_apply
                            );
                          }}
                          fullWidth
                          size="small"
                          label={isInternal ? 'Internal Tag' : 'Participant Tag'}
                          variant="filled"
                          clearOnBlur={false}
                          multiple={false}
                          noOptionsText={(tagQueryByRule[rule.id] || '').trim()
                            ? `No results for "${(tagQueryByRule[rule.id] || '').trim()}"${isInternal ? ', press Enter to create' : ''}`
                            : 'Type to search'}
                          getOptionLabel={(opt) => {
                            if (!opt) return '';
                            if (typeof opt !== 'object') {
                              return "Tag ID:" + opt;
                            }
                            if (!opt.name) {
                              return '';
                            }
                            return opt.category?.name ? opt.category.name + ": " + opt.name : opt.name;
                          }}
                          isOptionEqualToValue={(opt, val) => tagsEqual(opt, val)}
                          onQueryChange={(q) =>
                            setTagQueryByRule(prev => ({ ...prev, [rule.id]: q }))
                          }
                          onKeyDown={async (e) => {
                            if (!isInternal) return;
                            if (e.key !== 'Enter') return;
                            const q = (tagQueryByRule[rule.id] || '').trim();
                            if (!q) return;

                            const options = (tagOptionsByRule[rule.id] || []);
                            const match = options.find(o => tagsEqual(o, { name: q }));
                            if (match) {
                              updateDraft(rule.id, { tag_to_apply: normalizeTag(match) });
                              setTagQueryByRule(prev => ({ ...prev, [rule.id]: '' }));
                              setAcKeyByRule(k => ({ ...k, [rule.id]: (k[rule.id] || 0) + 1 }));
                              return;
                            }

                            try {
                              const created = await createInternalTag(q);
                              if (!created?.id) { throw new Error('Tag creation failed'); }
                              setTagOptionsByRule((o) => ({
                                ...o,
                                [rule.id]: [...(o[rule.id] || []), created],
                              }));
                              updateDraft(rule.id, { tag_to_apply: created });
                              setTagQueryByRule(prev => ({ ...prev, [rule.id]: '' }));
                              setAcKeyByRule(k => ({ ...k, [rule.id]: (k[rule.id] || 0) + 1 }));
                              enqueueSnackbar(`Tag "${created.name}" created`, { variant: 'success' });
                            } catch {
                              enqueueSnackbar('Tag could not be created', { variant: 'error' });
                            }
                          }}
                          textFieldProps={{
                            required: true,
                            error: shouldShowErrors(rule.id) && !normalizeTag(draft.tag_to_apply).id,
                            helperText: shouldShowErrors(rule.id) && !normalizeTag(draft.tag_to_apply).id ? 'Required' : '',
                          }}
                        />
                        </Box>
                      ) : (
                        <Chip label={tagToName(rule.tag_to_apply)} size="small" variant="outlined" />
                      )}
                    </Box>

                    {/* Enabled */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Enabled</Typography>
                      <Switch
                        size="small"
                        checked={!!(draft?.is_enabled ?? rule.is_enabled)}
                        onChange={(e) => toggleRuleEnabled(rule, e.target.checked)}
                        disabled={!hasAIwritePerm}
                      />
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <input type="checkbox" checked={!!allSelected} onChange={(e) => toggleAll(e.target.checked)} disabled={!hasAIdeletePerm} />
                </TableCell>
                {columns.map((col) => (
                  <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => {
                        const isAsc = orderBy === col.id && order === 'asc';
                        setOrder(isAsc ? 'desc' : 'asc');
                        setOrderBy(col.id);
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedRules.length === 0 && searchQuery.trim() ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                      No rules match your search
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedRules.map((rule) => {
                  const isEditing = editingId === rule.id;
                  const draft = drafts[rule.id] ?? rule;

                  const isInternal = (draft.tag_type || '').toLowerCase().includes('internal');

                  return (
                    <TableRow key={rule.id} hover>
                      <TableCell padding="checkbox">
                        <input type="checkbox" checked={!!selection[rule.id]} onChange={() => toggleOne(rule.id)} disabled={!hasAIdeletePerm} />
                      </TableCell>

                      {/* Trigger Type */}
                      <TableCell sx={{ minWidth: 160 }}>
                        {isEditing ? (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={draft.trigger_type}
                              onChange={(e) => updateDraft(rule.id, { trigger_type: e.target.value })}
                              disabled={!hasAIwritePerm}
                            >
                              <MenuItem value="Keyword">Keyword</MenuItem>
                              <MenuItem value="Sentiment">Sentiment</MenuItem>
                              <MenuItem value="Intent">Intent</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body2">{rule.trigger_type}</Typography>
                        )}
                      </TableCell>

                      {/* Trigger Value */}
                      <TableCell sx={{ minWidth: 240 }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            type={isSentimentType(draft.trigger_type) ? 'number' : 'text'}
                            multiline={!isSentimentType(draft.trigger_type)}
                            minRows={2}
                            maxRows={4}
                            inputProps={isSentimentType(draft.trigger_type) ? { min: 1, max: 10, step: 1 } : {}}
                            placeholder={
                              isSentimentType(draft.trigger_type) ? '1 – 10' : 
                              (draft.trigger_type === 'Keyword' ? 'e.g., Cancel reservation' : 'Value')
                            }
                            value={draft.trigger_value}
                            onChange={(e) => updateDraft(rule.id, { trigger_value: e.target.value })}
                            disabled={!hasAIwritePerm}
                            fullWidth
                            required
                            error={shouldShowErrors(rule.id) && (
                              !draft.trigger_value?.toString().trim() ||
                              (isSentimentType(draft.trigger_type) && !isValidSentimentValue(draft.trigger_value))
                            )}
                            helperText={shouldShowErrors(rule.id) && (
                              !draft.trigger_value?.toString().trim()
                                ? 'Required'
                                : (isSentimentType(draft.trigger_type) && !isValidSentimentValue(draft.trigger_value))
                                  ? 'Enter a whole number from 1 to 10'
                                  : ''
                            )}
                          />
                        ) : (
                          <Typography variant="body2">{rule.trigger_value}</Typography>
                        )}
                      </TableCell>

                      {/* Tag type */}
                      <TableCell sx={{ minWidth: 160 }}>
                        {isEditing ? (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={draft.tag_type}
                              onChange={(e) => {
                                updateDraft(rule.id, {
                                  tag_to_apply: null,
                                  tag_type: e.target.value,
                                  participant_tag_to_apply: null
                                });
                                setTagQueryByRule((q) => ({ ...q, [rule.id]: '' }));
                                setAcKeyByRule((k) => ({ ...k, [rule.id]: (k[rule.id] || 0) + 1 }));
                              }}
                            >
                              <MenuItem value="Internal tag">
                                Internal tag
                              </MenuItem>
                              <MenuItem value="Participant tag">
                                Participant tag
                              </MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body2">
                            {rule.tag_type}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Tag to Apply (RemoteAutocomplete) */}
                      <TableCell sx={{ minWidth: 260 }}>
                        {isEditing ? (
                          <RemoteAutocomplete
                            disabled={!hasAIwritePerm}
                            key={`${rule.id}-${(draft.tag_type || '').toLowerCase()}-${acKeyByRule[rule.id] || 0}`}
                            resource={TAGS_RESOURCE}
                            // filter strictly to internal tags when "Internal tag" is selected
                            apiFilters={[{ field: 'category_code', op: isInternal ? 'equals' : 'not', value: INTERNAL_TAG_TYPE }]}
                            defaultValue={normalizeTag(draft.tag_to_apply)}
                            onChange={(e, v) => {
                              const sel = v ? normalizeTag(v) : null;
                              updateDraft(rule.id, { tag_to_apply: sel });
                              ensureOptionPresent(rule.id, sel);
                            }}
                            setOptionsCount={() => {}}
                            setOptions={(opts) => {
                              const norm = (opts || []).map(normalizeTag);
                              setTagOptionsByRule((o) => ({ ...o, [rule.id]: norm }));
                              ensureOptionPresent( 
                                rule.id,
                                drafts[rule.id]?.tag_to_apply || rule.tag_to_apply
                              );
                            }}
                            fullWidth
                            size="small"
                            label={isInternal ? 'Internal Tag' : 'Participant Tag'}
                            variant="filled"
                            clearOnBlur={false}
                            multiple={false}
                            noOptionsText={(tagQueryByRule[rule.id] || '').trim()
                              ? `No results for "${(tagQueryByRule[rule.id] || '').trim()}"${isInternal ? ', press Enter to create' : ''}`
                              : 'Type to search'}
                            getOptionLabel={(opt) => {
                              if (!opt) return '';
                              // If it's a primitive (string/number), treat as ID
                              if (typeof opt !== 'object') {
                                return "Tag ID:" + opt;
                              }
                              // If it's an object but has no name, return empty string
                              if (!opt.name) {
                                return '';
                              }
                              return opt.category?.name ? opt.category.name + ": " + opt.name : opt.name;
                            }}
                            isOptionEqualToValue={(opt, val) => tagsEqual(opt, val)}
                            // keep the input text stable
                            //inputValue={tagQueryByRule[rule.id] || ''}
                            //onInputChange={(e, value) =>
                            //  setTagQueryByRule(prev => ({ ...prev, [rule.id]: value }))
                            //}
                            onQueryChange={(q) =>
                              setTagQueryByRule(prev => ({ ...prev, [rule.id]: q }))
                            }
                            // allow new tag creation on Enter for INTERNAL only
                            onKeyDown={async (e) => {
                              if (!isInternal) return;
                              if (e.key !== 'Enter') return;
                              const q = (tagQueryByRule[rule.id] || '').trim();
                              if (!q) return;

                              const options = (tagOptionsByRule[rule.id] || []);
                              const match = options.find(o => tagsEqual(o, { name: q }));
                              if (match) {
                                updateDraft(rule.id, { tag_to_apply: normalizeTag(match) });                       // keep
                                setTagQueryByRule(prev => ({ ...prev, [rule.id]: '' }));                           // keep
                                setAcKeyByRule(k => ({ ...k, [rule.id]: (k[rule.id] || 0) + 1 }));                 // <-- **NEW** force remount to show selected
                                return;
                              }

                              try {
                                const created = await createInternalTag(q);
                                if (!created?.id) { throw new Error('Tag creation failed'); }
                                setTagOptionsByRule((o) => ({
                                  ...o,
                                  [rule.id]: [...(o[rule.id] || []), created],
                                }));
                                updateDraft(rule.id, { tag_to_apply: created });                                    // keep
                                setTagQueryByRule(prev => ({ ...prev, [rule.id]: '' }));                            // keep
                                setAcKeyByRule(k => ({ ...k, [rule.id]: (k[rule.id] || 0) + 1 }));                 // <-- **NEW** force remount to show selected
                                enqueueSnackbar(`Tag "${created.name}" created`, { variant: 'success' });          // keep
                              } catch {
                                enqueueSnackbar('Tag could not be created', { variant: 'error' });                 // keep
                              }
                            }}
                            // Show an error only after save attempt if nothing selected
                            textFieldProps={{
                              required: true,
                              error: shouldShowErrors(rule.id) && !normalizeTag(draft.tag_to_apply).id,
                              helperText: shouldShowErrors(rule.id) && !normalizeTag(draft.tag_to_apply).id ? 'Required' : '',
                            }}
                          />
                        ) : (
                          <Chip label={tagToName(rule.tag_to_apply)} size="small" variant="outlined" />
                        )}
                      </TableCell>

                      {/* Enabled (per-rule) */}
                      <TableCell>
                        {/* **ADD** wrapper to center the switch */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                          <Switch
                            size="small"
                            checked={!!(draft?.is_enabled ?? rule.is_enabled)}
                            onChange={(e) => toggleRuleEnabled(rule, e.target.checked)}
                            disabled={!hasAIwritePerm}
                          />
                        </Box>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={() => cancelEdit(rule.id)} title="Cancel">
                                <Close fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => saveRule(rule.id)} disabled={!hasAIwritePerm}>
                                <Save fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => startEdit(rule)} disabled={!hasAIwritePerm}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <DeleteIconButton onClick={() => openConfirm(rule)} allowed={hasAIdeletePerm} />
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Single delete confirm */}
      <Dialog open={confirmOpen} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Delete rule?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This will permanently remove the rule{pendingRule?.trigger_value ? (
              <> for <strong>{pendingRule.trigger_type}</strong> “{pendingRule.trigger_value}”</>
            ) : null}. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <DeleteButton
            onClick={() => {
              const r = pendingRule;
              closeConfirm();
              if (r) deleteRule(r);
            }}
            disabled={!hasAIdeletePerm}
          >
            Delete
          </DeleteButton>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirm */}
      <Dialog open={confirmBulkOpen} onClose={closeBulkConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Delete selected rules?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            You’re about to delete {Object.values(selection).filter(Boolean).length} rule(s).
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkConfirm}>Cancel</Button>
          <DeleteButton
            onClick={() => {
              closeBulkConfirm();
              bulkDelete();
            }}
            disabled={!hasAIdeletePerm}
          >
            Delete
          </DeleteButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AutoTaggingRulesTab;
